import { useState, useContext, useEffect } from 'react';
import AuthContext from '../../../contexts/auth/AuthContext';
import ProcessContext from "../../../contexts/process/ProcessContext";
import UIContext from '../../../contexts/ui/UIContext';
import { useParams, useNavigate } from 'react-router-dom';
import StatusBarButton from "../../layout/StatusBarButton";
import Loading from '../../ui-elements/Loading';
import DevicesApiService from "../../../services/apiServices/DevicesApiService";
import Devices from './Device';
import FarmhandInstallationsApiService from "../../../services/apiServices/FarmhandInstallationsApiService";
import { HELP_DESK_LINK } from '../../../constants/helpDesk';
import { separateOnCapitalLetters } from '../../../utility/PascalCaseSeparator';
import HarvestingProceduresContext from '../../../contexts/management/harvestingProcedures/HarvestingProceduresContext';
import HarvestingProceduresApiService from '../../../services/apiServices/HarvestingProceduresApiService';

const Detail = () => {
    const params = useParams();
    const { containerId } = params;
    const navigate = useNavigate();
    const { currentTenant, accessibleContainers } = useContext(AuthContext);
    const { notifySuccess, notifyWarning, notifyError } = useContext(ProcessContext);
    const { allHarvestingProcedures } = useContext(HarvestingProceduresContext);
    const [containerHarvestingProcedures, setContainerHarvestingProcedures] = useState();
    const { setInfo } = useContext(UIContext);
    const [loading, setLoading] = useState(false);
    const [currentContainer, setCurrentContainer] = useState(null);
    const [storageAdivceRooted, setStorageAdviceRooted] = useState(null);
    const [storageAdivceCut, setStorageAdviceCut] = useState(null);
    const [farmHandInstallationName, setFarmHandInstallationName] = useState("");
    const [weighingCameraState, setWeighingCameraState] = useState({});
    const [robotState, setRobotState] = useState({});

    useEffect(() => {
        const initialLoad = async () => {
            await load();
        };

        initialLoad();
    }, []);

    useEffect(() => {
        if (currentContainer) {
            setInfo(`${currentContainer?.name}`);
            setLoading(true);
            DevicesApiService.getDeviceState(currentTenant?.id, currentContainer?.id, currentContainer?.weighingCameraId)
                .then((cameraResult) => {
                    let state = cameraResult?.data?.data === 1 ? true : false;
                    setWeighingCameraState(state);
                });
            DevicesApiService.getDeviceState(currentTenant?.id, currentContainer?.id, currentContainer?.robotId)
                .then((robotResult) => {
                    let state = robotResult?.data?.data === 1 ? true : false;
                    setRobotState(state);
                });
            setLoading(false);
        }
    }, [currentContainer]);

    const load = async () => {
        setLoading(true);
        var container = accessibleContainers.find(c => c.id == containerId);
        setCurrentContainer(container);
        var harvestingProcedures = await HarvestingProceduresApiService.getManyHarvestingProcedures(currentTenant?.id, container?.id);
        var rootedHarvestingProcedure = harvestingProcedures.data.data.find(hp => hp.type === "SingleHarvestRooted");
        if (rootedHarvestingProcedure) {
            setStorageAdviceRooted(rootedHarvestingProcedure.storageAdvice);
        }

        var cutHarvestingProcedure = harvestingProcedures.data.data.find(hp => hp.type === "SingleHarvestCut" || hp.type === "MultiHarvestCut");
        if (cutHarvestingProcedure) {
            setStorageAdviceCut(cutHarvestingProcedure.storageAdvice);
        }
        setContainerHarvestingProcedures(harvestingProcedures.data.data);

        if (container.farmHandInstallationId) {
            await FarmhandInstallationsApiService.getFarmHandInstallationById(container.farmHandInstallationId)
                .then((response) => {
                    setFarmHandInstallationName(response.data.data.name);
                })
                .catch((error) => {
                    console.error(error);
                    notifyError(<>We have encountered an issue on our side while loading farm hand. Please reload the page. If the error still exists, please contact {HELP_DESK_LINK}.</>);
                });
        }
        setLoading(false);
    };

    const containsCutProcedure = allHarvestingProcedures.some(hp => hp.type.includes("Cut"));
    const containsRootProcedure = allHarvestingProcedures.some(hp => hp.type.includes("Root"));

    const onHandleEdit = async () => {
        navigate(`/management/containers/${containerId}/edit`);
    };

    const copyTextToClipboard = (text) => {
        if (!navigator.clipboard) {
            fallbackCopyTextToClipboard(text);
            return;
        }
        navigator.clipboard.writeText(text).then(
            function () {
                notifySuccess('Copied to clipboard.');
            },
            function () {
                notifyWarning('Could not copy id to clipboard');
            }
        );
    };

    const fallbackCopyTextToClipboard = (text) => {
        var textArea = document.createElement('textarea');
        textArea.value = text ? text : '';

        textArea.style.top = '0';
        textArea.style.left = '0';
        textArea.style.position = 'fixed';

        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            const successful = document.execCommand('copy');
            if (!successful) {
                notifyWarning('Could not copy id to clipboard');
            }
        } catch {
            notifyWarning('Could not copy id to clipboard');
        }

        document.body.removeChild(textArea);
    };

    return loading ? (
        <Loading fullScreen={true} />
    ) : (
        <>
            <div className='container-management-detail-container'>
                <div className="container-management-detail-card-container">
                    <div className="container-management-detail-card">
                        <div className='edit-container-title'>General Information</div>

                        <div className="container-name-icon">
                            <i className="fa-solid fa-signature"></i>
                        </div>
                        <div className="container-name-label">Name</div>
                        <div className="container-name-value">
                            {currentContainer?.name}
                        </div>

                        <div className="container-location-icon">
                            <i className="fa-solid fa-location-dot"></i>
                        </div>
                        <div className="container-location-label">Location</div>
                        <div className="container-location-details">
                            {currentContainer?.location?.latitude && currentContainer?.location?.longitude ? (
                                <>
                                    {currentContainer.location.street} {currentContainer.location.number}, {currentContainer.location.postalCode} {currentContainer.location.city}
                                    <br />
                                    {currentContainer.location.country}
                                </>
                            ) : (
                                ""
                            )}
                        </div>

                        <div className="container-description-icon">
                            <i className="fa-regular fa-comment-dots"></i>
                        </div>
                        <div className="container-description-label">Description</div>
                        <div className="container-description-value">
                            {currentContainer?.description}
                        </div>

                        <Devices
                            devices={[
                                {
                                    icon: 'fas fa-balance-scale',
                                    label: 'Scale',
                                    value: currentContainer?.scaleId.toLowerCase(),
                                    className: 'container-management-details-scale',
                                    copy: true,
                                    onCopy: () => copyTextToClipboard(currentContainer?.scaleId)
                                },
                                {
                                    icon: 'fas fa-print',
                                    label: 'Label Printer',
                                    value: currentContainer?.labelPrinterId.toLowerCase(),
                                    className: 'container-management-details-label-printer',
                                    copy: true,
                                    onCopy: () => copyTextToClipboard(currentContainer?.labelPrinterId)
                                },
                                {
                                    icon: 'fas fa-camera',
                                    label: 'Bird\'s Eye-View Camera',
                                    value: currentContainer?.weighingCameraId.toLowerCase(),
                                    className: 'container-management-details-weighing-camera',
                                    statusIcon: weighingCameraState ? 'fas fa-circle' : 'far fa-circle',
                                    copy: true,
                                    onCopy: () => copyTextToClipboard(currentContainer?.weighingCameraId)
                                },
                                {
                                    icon: 'fas fa-robot',
                                    label: 'Robot',
                                    value: currentContainer?.robotId.toLowerCase(),
                                    className: 'container-management-details-robot',
                                    statusIcon: robotState ? 'fas fa-circle' : 'far fa-circle',
                                    copy: true,
                                    onCopy: () => copyTextToClipboard(currentContainer?.robotId)
                                }
                            ]}
                        />

                        <div className="container-misc-title">Miscellaneous</div>

                        <div className="container-farmhand-icon">
                            <i className="fa-solid fa-tractor"></i>
                        </div>
                        <div className="container-farmhand-label">Farmhand ID</div>
                        <div className="container-farmhand-value">
                            {farmHandInstallationName}
                        </div>

                        <div className={containsRootProcedure ? "container-root-advice-icon" : "container-root-advice-icon disable"}>
                            <i className="fa-solid fa-box-archive"></i>
                        </div>
                        <div className={containsRootProcedure ? "container-root-advice-label" : "container-root-advice-label disable"}>
                            Storage Advice Text for Root Produces
                        </div>
                        <div className={containsRootProcedure ? "container-root-advice-value" : "container-root-advice-value disable"}>
                            {storageAdivceRooted}
                        </div>

                        <div className={containsCutProcedure ? "container-cut-advice-icon" : "container-cut-advice-icon disable"}>
                            <i className="fa-solid fa-box-archive"></i>
                        </div>
                        <div className={containsCutProcedure ? "container-cut-advice-label" : "container-cut-advice-label disable"}>
                            Storage Advice Text for Cut Produces
                        </div>
                        <div className={containsCutProcedure ? "container-cut-advice-value" : "container-cut-advice-value disable"}>
                            {storageAdivceCut}
                        </div>

                        <div className="container-procedures-icon">
                            <i className="fa-solid fa-wheat-awn"></i>
                        </div>
                        <div className="container-procedures-label">Harvesting Procedures</div>
                        <div className="container-procedures-value">
                            <div className="container-procedures-list">
                                {containerHarvestingProcedures?.map((item, index) => (
                                    <div className="container-procedures-item" key={index}>
                                        <span className="container-procedure-name">
                                            {separateOnCapitalLetters(item.type)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <StatusBarButton
                    label='Done'
                    clickHandler={() => navigate('/management/containers/list')}
                    statusSlot={3}
                    icon='fair fai-circle-check'
                    type='inline'
                />
                <StatusBarButton
                    label='Edit'
                    clickHandler={onHandleEdit}
                    statusSlot={5}
                    reversed={true}
                    icon='fas fa-pen'
                    type='inline'
                />
            </div>
        </>
    );
};

export default Detail;
